/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import moment from 'moment';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { datetime, expr } from '@codegrade/utils';
import ExamSessionFooter from '@/components/ExamSessionFooter';
import ExamSessionPill from '@/components/ExamSessionPill';
import FooterBar from '@/components/FooterBar';
import Loader from '@/components/Loader';
import PageTitle from '@/components/PageTitle';
import Sidebar from '@/components/Sidebar';
import StudentViewFooter from '@/components/StudentViewFooter';
// @ts-expect-error This component is not yet in TS
import SubmitButton from '@/components/SubmitButton';
import * as models from '@/models';
import { CoursesStore, SiteSettingsStore, UIPrefsStore, UserStore, waitTillStoreCreated, } from '@/store';
import * as utils from '@/utils';
import './style.less';
export default tsx.component({
    name: 'app',
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            loading: true,
            didLoginCheck: false,
            toasts: [],
            siteSettingsTimer: Maybe.empty(),
            sessionWillExpireModalHidden: false,
        };
    },
    computed: {
        /** @returns If dark mode is enabled. */
        darkMode() {
            return UIPrefsStore.darkMode();
        },
        /** @returns The tenant id of the logged in user, if someone is logged in. */
        loggedInTenantId() {
            return UserStore.tenantId();
        },
        /** @returns The amount of time before the session expires toast will be shown. */
        sessionWillExpireToastTimeout() {
            return this.loggedInTenantId.caseOf({
                Just: tid => SiteSettingsStore.getSetting()('ACCESS_TOKEN_TOAST_WARNING_TIME', tid),
                Nothing: () => SiteSettingsStore.getGlobalSetting()('ACCESS_TOKEN_TOAST_WARNING_TIME'),
            });
        },
        /** @returns The amount of time before the session expires when we will show the modal warning the user about this. */
        sessionWillExpireModalTimeout() {
            return this.loggedInTenantId.caseOf({
                Just: tid => SiteSettingsStore.getSetting()('ACCESS_TOKEN_MODAL_WARNING_TIME', tid),
                Nothing: () => SiteSettingsStore.getGlobalSetting()('ACCESS_TOKEN_MODAL_WARNING_TIME'),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        canManageLTICourse() {
            if (this.$LTICourseId == null) {
                return false;
            }
            return CoursesStore.getCourse()(this.$LTICourseId)
                .filter(c => c instanceof models.Course)
                .mapOrDefault(course => course.canManage, false);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showSidebar() {
            if (this.$route.meta?.hideSidebar ?? false) {
                return false;
            }
            if (this.$inLTI) {
                return this.canManageLTICourse;
            }
            return true;
        },
        /** @returns The course id that the user is logged in for, if any. */
        forCourseId() {
            return UserStore.dangerousSession()
                .chainNullable(session => session.restrictions.forContext?.courseId)
                .filter(() => UserStore.studentView().tag === 'disabled')
                .extract();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        forCourse() {
            if (this.forCourseId == null) {
                return null;
            }
            return CoursesStore.getCourse()(this.forCourseId).mapOrDefault(course => course.name, null);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        serverTimeOutOfSync() {
            return SiteSettingsStore.getServerTimeOffset().mapOrDefault(diff => {
                const maxDiff = moment.duration(5, 'minutes');
                return Math.abs(diff.asSeconds()) > maxDiff.asSeconds();
            }, false);
        },
        /** @returns Whether the student view border should be shown. */
        isStudentView() {
            return UserStore.studentView().tag !== 'disabled';
        },
        /** @returns The exam session label for the top pill, or undefined if not in exam. */
        examPillLabel() {
            return UserStore.loggedInUser().chain((user) => {
                const examState = user.getExamSessionState();
                return expr.match(examState, 'tag', {
                    disabled: () => Maybe.empty(),
                    enabled: () => Maybe.of(`Exam session — ${user.name.orDefault('Unknown')}`),
                });
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        mainContentNode() {
            return utils.ifExpr(this.loading, () => [
                <PageTitle pageTitle="Opening"/>,
                <Loader class="main-page-loader" pageLoader/>,
            ], () => [
                utils.ifOrEmpty(this.showSidebar, () => <Sidebar ref="sidebar"/>),
                <main class={[
                        'flex flex-col justify-between',
                        // Clear the width of the sidebar and set width to maximum, minus width of sidebar.
                        this.showSidebar ? 'ml-12 w-[calc(100%-var(--spacing)*12)]' : 'w-full',
                        // Set height to fit content.
                        'h-fit min-h-screen',
                        this.examPillLabel.isJust() && 'cg-exam-session-border',
                        this.isStudentView && 'cg-student-view-border',
                    ]}>
                        {utils.ifJustOrEmpty(this.examPillLabel, label => (<ExamSessionPill label={label}/>))}

                        {/* We have an extra data element, `didLoginCheck`, to make sure we
                    first render the sidebar, to make sure the logo request (for the
                    svg) is done first, as otherwise the max requests the browser can do
                    is reached, and it is empty for quite a long time. However, we still
                    need this container-fluid div, as otherwise the sidebar will not be
                    in the correct position. */}
                        {utils.ifExpr(this.didLoginCheck, () => (<router-view />), () => (<div class="grow"></div>))}

                        {this.maybeFooterNode.orDefaultLazy(utils.emptyVNode)}
                        <StudentViewFooter />
                        <ExamSessionFooter />
                    </main>,
            ]);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeFooterNode() {
            if (this.$route.meta.hideFooter) {
                return Maybe.empty();
            }
            return Maybe.of(<FooterBar />);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        frameBorderNode() {
            return utils.ifOrEmpty(window !== window.top && this.$ltiProvider?.addBorder === true, () => <div class="app-frame-border border"/>);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        toastsNode() {
            const toasts = this.sessionExpirationToast.toList().concat(this.toasts);
            return toasts.map(toast => (<b-toast key={toast.tag} class={`cg-toast-${toast.tag}`} toaster="b-toaster-top-right" variant={this.getToastVariant(toast)} title={toast.title} href={toast.href} visible noAutoHide={toast.hideAfter.isNothing()} autoHideDelay={toast.hideAfter.map(x => x.asMilliseconds()).extract()} solid onHide={() => this.deleteToast(toast)}>
                    {utils.ifExpr(toast.onClick != null, () => (<div onClick={toast.onClick}>{toast.message}</div>), () => toast.message)}
                </b-toast>));
        },
        /**
         * @returns A toast with a warning that the user's session will expire
         * soon. If the modal with the same message is currently visible, the
         * toast will be hidden.
         */
        sessionExpirationToast() {
            if (this.sessionWillExpireModalVisible) {
                return Maybe.empty();
            }
            return this.sessionExpiration
                .filter(({ expiresIn }) => {
                const ms = expiresIn.asMilliseconds();
                return ms > 0 && ms < this.sessionWillExpireToastTimeout.asMilliseconds();
            })
                .map(sessionExpiration => ({
                tag: 'session-will-expire',
                title: 'Session will expire',
                message: this.getSessionWillExpireText(sessionExpiration),
                variant: 'warning',
                hideAfter: Maybe.empty(),
            }));
        },
        /** @returns When the current session of the logged in user expires. */
        sessionExpiration() {
            const user = UserStore.loggedInUser().extract();
            if (user == null) {
                return Maybe.empty();
            }
            const expiresAt = user.session.expiresAt;
            const now = this.$getRootInstance().$getNow(expiresAt, {
                secondsPrecisionNeeded: this.sessionWillExpireToastTimeout.asSeconds() + 60,
            });
            return Maybe.of({
                expiresIn: datetime.diff(now, expiresAt).subtract(1, 'minute'),
                // If the login method is unknown, just assume username and password.
                loginMethod: user.loginMethod.orDefault({
                    tag: 'username-password',
                    tenantId: Maybe.of(user.tenantId),
                }),
            });
        },
        /**
         * @returns A modal warning the user that their session will expire
         * soon, or that their session has already expired.
         */
        sessionExpirationModalNode() {
            return utils.ifJustOrEmpty(this.sessionExpiration, sessionExpiration => {
                const ms = sessionExpiration.expiresIn.clone().asMilliseconds();
                if (ms > this.sessionWillExpireModalTimeout.asMilliseconds()) {
                    return utils.emptyVNode();
                }
                if (ms > 0) {
                    return this.renderSessionWillExpireModal(sessionExpiration);
                }
                return this.renderSessionHasExpiredModal(sessionExpiration);
            });
        },
        /** @returns Should we show the "Your session will expire soon" modal. */
        sessionWillExpireModalVisible() {
            if (this.sessionWillExpireModalHidden) {
                return false;
            }
            const expiration = this.sessionExpiration.extract();
            if (expiration == null) {
                return false;
            }
            return (expiration.expiresIn.asMilliseconds() <
                this.sessionWillExpireModalTimeout.asMilliseconds());
        },
    },
    watch: {
        darkMode: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                if (this.darkMode) {
                    document.body.classList.add('cg-dark-mode');
                }
                else {
                    document.body.classList.remove('cg-dark-mode');
                }
            },
        },
        $inLTI: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                if (this.$inLTI) {
                    document.body.classList.add('cg-in-lti');
                }
                else {
                    document.body.classList.remove('cg-in-lti');
                }
            },
        },
        forCourse: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler(newValue, oldValue) {
                if (oldValue != null) {
                    this.deleteToast(this.makeForCourseToast(oldValue));
                }
                if (newValue != null) {
                    this.addToast(this.makeForCourseToast(newValue));
                }
            },
        },
        forCourseId: {
            immediate: true,
            /** Called when the `forCourseId` computed value changes. */
            handler() {
                const courseId = this.forCourseId;
                if (courseId != null) {
                    CoursesStore.loadSingleCourse({ courseId });
                }
            },
        },
        serverTimeOutOfSync: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                this.maybeShowServerTimeToast();
            },
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        loggedInTenantId() {
            utils.loadBackgroundPromises(this.loggedInTenantId.caseOf({
                Just: tenantId => SiteSettingsStore.loadTenantSettings({ tenantId }),
                Nothing: () => SiteSettingsStore.loadSiteSettings({}),
            }));
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeShowServerTimeToast() {
            const toast = this.makeServerTimeToast();
            if (this.serverTimeOutOfSync) {
                this.addToast(toast);
                this.pollSiteSettings();
            }
            else {
                this.deleteToast(toast);
                this.clearSiteSettingsTimer();
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        pollSiteSettings() {
            this.clearSiteSettingsTimer();
            const timeout = moment.duration(5, 'minutes');
            const timerId = setTimeout(() => {
                utils.loadBackgroundPromises(SiteSettingsStore.loadSiteSettings({ forLoggedInUser: true }).finally(() => {
                    if (this.serverTimeOutOfSync &&
                        timerId === this.siteSettingsTimer.extract()) {
                        this.pollSiteSettings();
                    }
                }));
            }, timeout.asMilliseconds());
            this.siteSettingsTimer = Maybe.of(timerId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        clearSiteSettingsTimer() {
            this.siteSettingsTimer.ifJust(timer => {
                clearTimeout(timer);
                this.siteSettingsTimer = Maybe.empty();
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        makeServerTimeToast() {
            const message = `We have detected a difference between the time on our servers
                and the time in your browser. Please check that the date & time
                settings on your computer are correct. We will try to correct
                for the measured difference.`;
            return {
                tag: 'serverTime',
                title: 'Time synchronization issue detected',
                message,
                variant: 'warning',
                hideAfter: Maybe.empty(),
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        makeForCourseToast(courseName) {
            return {
                tag: 'forCourse',
                title: 'Limited login enabled',
                message: `You are currently only logged in for the course "${courseName}".
                    This means you can only see data from this course.`,
                variant: 'warning',
                hideAfter: Maybe.empty(),
            };
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        addToast(toast) {
            if (this.toasts.every(other => other.tag !== toast.tag)) {
                const hideAfter = toast.hideAfter ?? Maybe.empty();
                this.toasts.push({ ...toast, hideAfter });
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        deleteToast(toast) {
            this.deleteToastByTag(toast.tag);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        deleteToastByTag(tag) {
            this.toasts = this.toasts.filter(t => t.tag !== tag);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async doLogin() {
            await waitTillStoreCreated();
            this.loading = false;
            if (this.showSidebar) {
                await this.$afterRerender();
            }
            this.didLoginCheck = true;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getToastVariant(toast) {
            if (toast.variant != null) {
                return toast.variant;
            }
            return this.darkMode ? 'dark' : '';
        },
        /**
         * Render the modal warning the user that their session will expire soon.
         *
         * @param sessionExpiration Information about the expiration of this session.
         * @returns A modal with a warning that the session will expire soon, a
         * button to log out and redirect to the login page, and a button to
         * hide this modal.
         */
        renderSessionWillExpireModal({ expiresIn, loginMethod }) {
            const str = datetime.formatMoment(moment(expiresIn.asMilliseconds()), 'mm:ss');
            return (<b-modal visible={this.sessionWillExpireModalVisible} noCloseOnEsc noCloseOnBackdrop hideFooter title="Session will expire" onHidden={() => {
                    this.sessionWillExpireModalHidden = true;
                }}>
                    <p>
                        Your session will expire in {str} minutes. Please save any unsaved changes.
                    </p>

                    <p>{this.renderLtiReopenMessage(loginMethod)}</p>

                    <div class="d-flex justify-content-between">
                        <b-button aria-label="Hide this modal to save your work" onClick={() => {
                    this.sessionWillExpireModalHidden = true;
                }}>
                            Hide
                        </b-button>

                        {this.renderLoginMethod(loginMethod)}
                    </div>
                </b-modal>);
        },
        /**
         * Get the message warning the user that their session will expire soon.
         *
         * @param sessionExpiration The expiration data to use.
         * @returns A message warning that the session will expire.
         */
        getSessionWillExpireText(sessionExpiration) {
            const str = datetime.formatMoment(moment(sessionExpiration.expiresIn.asMilliseconds()), 'mm:ss');
            const loginRoute = this.getLoginRoute(sessionExpiration.loginMethod).extract();
            if (loginRoute == null) {
                return (<span>
                        Your session will expire in {str} minutes. Please save any unsaved changes
                        you have made and re-open the assignment.
                    </span>);
            }
            return (<span>
                    Your session will expire in {str} minutes. Please save any unsaved changes you
                    have made and{' '}
                    <a href={loginRoute.fullPath} onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    this.logoutAndRedirect(loginRoute);
                }} class="inline-link cursor-pointer">
                        click here
                    </a>{' '}
                    to log back in.
                </span>);
        },
        /**
         * Render a modal warning the user that their session has expired.
         *
         * It is impossible to close this modal, the only action is a button
         * that the user can click to go back to the login page.
         *
         * @param loginMethod The method the user used to log in.
         * @returns A modal warning the user that their session has expired.
         */
        renderSessionHasExpiredModal({ loginMethod }) {
            return (<b-modal visible noCloseOnEsc noCloseOnBackdrop hideHeaderClose hideFooter title="Session has expired">
                    <p>Your session has expired.</p>

                    <p>{this.renderLtiReopenMessage(loginMethod)}</p>

                    <div class="float-right">{this.renderLoginMethod(loginMethod)}</div>
                </b-modal>);
        },
        /**
         * Render a button to go back to the login page, or a message to reload the assignment.
         *
         * @param loginMethod The method the user used to log in.
         * @returns A button or message as described above.
         */
        renderLoginMethod(loginMethod) {
            return expr.match(loginMethod, 'tag', {
                'email-and-otp': ({ tenantId }) => this.renderGoToLoginButton(this.getLoginPageRoute(Maybe.of(tenantId))),
                'username-password': ({ tenantId }) => this.renderGoToLoginButton(this.getLoginPageRoute(tenantId)),
                lti: () => utils.emptyVNode(),
                sso: ({ tenantId }) => this.renderGoToLoginButton(this.getLoginPageRoute(tenantId)),
                'assignment-login': ({ assignmentId, loginUuid }) => this.renderGoToLoginButton(this.getAssignmentLoginPageRoute(assignmentId, loginUuid)),
            });
        },
        /**
         * Render a message telling the user to reopen the assignment in their LMS.
         *
         * @param loginMethod The method the user used to log in.
         * @returns A message telling the user to reopen the assignment in their
         * LMS, if they logged in with LTI. Otherwise an empty string.
         */
        renderLtiReopenMessage(loginMethod) {
            return expr.match(loginMethod, 'tag', {
                lti: ({ lms }) => `Please reload this assignment in ${lms.orDefault('the LMS')}.`,
                sso: () => '',
                'username-password': () => '',
                'email-and-otp': () => '',
                'assignment-login': () => '',
            });
        },
        /**
         * Render the button to go back to the login page.
         *
         * @param route The route of the login page relevant for the current user.
         * @returns A button that goes to the given login page when clicked.
         */
        renderGoToLoginButton(route) {
            return (<SubmitButton aria-label="Log in again" variant="primary" submit={() => this.logoutAndRedirect(route)}>
                    Click here to log in again
                </SubmitButton>);
        },
        /**
         * Log the user out of the current session, and go to the given route.
         *
         * @param route The route to redirect to after logging out.
         */
        async logoutAndRedirect(route) {
            // The session is expiring (or has expired), so terminating an
            // active isolated session is acceptable without additional
            // confirmation.
            await UserStore.logout({
                revoke: true,
                signals: true,
                confirmSessionTermination: true,
            });
            this.sessionWillExpireModalHidden = false;
            this.$router.push(route);
        },
        /**
         * Get the route for the relevant login page.
         *
         * @param loginMethod The method the user used to log in.
         * @returns The route to log in the same way as the given login method.
         */
        getLoginRoute(loginMethod) {
            return expr.match(loginMethod, 'tag', {
                'email-and-otp': ({ tenantId }) => Maybe.of(this.getLoginPageRoute(Maybe.of(tenantId))),
                'username-password': ({ tenantId }) => Maybe.of(this.getLoginPageRoute(tenantId)),
                lti: () => Maybe.empty(),
                sso: ({ tenantId }) => Maybe.of(this.getLoginPageRoute(tenantId)),
                'assignment-login': ({ assignmentId, loginUuid }) => Maybe.of(this.getAssignmentLoginPageRoute(assignmentId, loginUuid)),
            });
        },
        /**
         * Get the route to the login page for the given tenant.
         *
         * @param tenantId The id of the tenant to log into.
         * @returns The route to the login page for the given tenant.
         */
        getLoginPageRoute(tenantId) {
            const params = tenantId
                .map((id) => ({
                tenantId: id,
            }))
                .orDefault({});
            return {
                name: 'login',
                params,
                query: {
                    redirect: this.$router.currentRoute.fullPath,
                },
            };
        },
        /**
         * Get the route to the login page for the given assignment.
         *
         * @param assignmentId The id of the assignment to log into.
         * @param loginUuid The id of the user-specific login link for this assignment.
         * @returns The route to the login page for the given assignment.
         */
        getAssignmentLoginPageRoute(assignmentId, loginUuid) {
            return {
                name: 'assignment_login',
                params: {
                    assignmentId: assignmentId.toString(10),
                    loginUuid,
                },
                query: {
                    redirect: this.$router.currentRoute.fullPath,
                },
            };
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    created() {
        if (this.$route.query.inLTI !== undefined) {
            this.$inLTI = true;
        }
        document.body.addEventListener('click', event => {
            if (event.target instanceof Element &&
                event.target.closest('.popover-body') == null &&
                event.target.closest('.sidebar') == null) {
                this.$root.$emit('cg::sidebar::close');
            }
        }, true);
        document.body.addEventListener('keyup', event => {
            if (event.key === 'Escape') {
                this.$root.$emit('bv::hide::popover');
            }
        }, true);
        this.$root.$on('cg::app::toast', this.addToast);
        this.$root.$on('cg::app::delete-toast', this.deleteToastByTag);
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    mounted() {
        this.doLogin();
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    destroyed() {
        this.$root.$off('cg::app::toast', this.addToast);
        this.$root.$off('cg::app::delete-toast', this.deleteToastByTag);
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        return (<div id="app" data-app="ready">
                {this.mainContentNode}
                {this.frameBorderNode}
                {this.sessionExpirationModalNode}
                {this.toastsNode}
            </div>);
    },
});
